import { Link } from 'react-router-dom';
import { MEMBERSHEEP_APP_STORE_URL } from '@/membersheep/config';
import { localizePath } from '@/travo/i18n/locale';
import { msPath } from '@/membersheep/i18n';
import travoLogo from '@/travo/assets/icons/logo-256.webp';
import memberSheepIcon from '@/membersheep/assets/membersheep-512.png';

const TILE =
  'group relative flex min-h-[420px] flex-col overflow-hidden rounded-[26px] p-7 motion-safe:animate-rise motion-safe:transition-[transform,box-shadow] motion-safe:duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(0,0,0,0.45)] md:min-h-[520px] md:rounded-[32px] md:p-10';
const CTA = 'mt-[18px] inline-flex items-center gap-2 font-semibold';
const CTA_ARROW = 'motion-safe:transition-transform group-hover:translate-x-1';

/** Travo's postmark: the same ring-and-waves cancel its own site uses, in stamp blue. */
const Postmark = () => (
  <svg
    viewBox="0 0 200 200"
    aria-hidden="true"
    className="absolute -top-6 -right-10 h-[190px] w-[190px] -rotate-12 text-[#4f5fe5] md:-top-[30px] md:-right-[50px] md:h-[230px] md:w-[230px]"
  >
    <circle cx="100" cy="100" r="88" fill="none" stroke="currentColor" strokeWidth="3" />
    <circle cx="100" cy="100" r="62" fill="none" stroke="currentColor" strokeWidth="2" />
    <defs>
      <path id="labs-postmark-ring" d="M100 100 m-75 0 a75 75 0 1 1 150 0 a75 75 0 1 1 -150 0" />
    </defs>
    <text fontFamily="'DM Mono', monospace" fontSize="13" letterSpacing="3" fill="currentColor">
      <textPath href="#labs-postmark-ring">TRAVOLABS.COM · WINDOW SEAT · TRAVO ·</textPath>
    </text>
    <path d="M30 96 q12 -10 24 0 t24 0 t24 0 t24 0 t24 0 t24 0" fill="none" stroke="currentColor" strokeWidth="2.5" />
    <path d="M30 110 q12 -10 24 0 t24 0 t24 0 t24 0 t24 0 t24 0" fill="none" stroke="currentColor" strokeWidth="2.5" />
  </svg>
);

const TravoTile = () => (
  <Link
    to={localizePath('en', '/')}
    className={`${TILE} bg-[#f6eedc] bg-[radial-gradient(rgba(43,42,51,0.07)_1px,transparent_1px)] bg-[length:6px_6px] font-['DM_Sans',sans-serif] text-[#2b2a33] [animation-delay:160ms]`}
  >
    <Postmark />
    <span className="h-[84px] w-[84px] rounded-[19px] border-2 border-[#2b2a33] bg-[#fffdf7] p-[5px] shadow-[4px_4px_0_#2b2a33]">
      <img src={travoLogo} alt="" width={70} height={70} className="h-full w-full rounded-[14px]" />
    </span>
    <h2 className="mt-7 font-['Shrikhand',serif] text-[52px] leading-none md:text-[64px]">Travo</h2>
    <p className="mt-3 max-w-[26ch] text-lg text-[#4a4740]">
      Life moves fast. Travo remembers — trips, check-ins and a passport that fills itself.
    </p>
    <div className="mt-auto flex flex-wrap gap-2 pt-7 text-[13px]">
      {['iOS', 'Android', 'Free', '5 languages'].map((tag) => (
        <span key={tag} className="rounded-lg border-[1.5px] border-[#2b2a33] bg-[#fffdf7] px-2.5 py-1">
          {tag}
        </span>
      ))}
    </div>
    <span className={`${CTA} text-[#4f5fe5]`}>
      Visit Travo <span className={CTA_ARROW}>→</span>
    </span>
    <span
      aria-hidden="true"
      className="absolute inset-x-0 bottom-0 h-2.5 bg-[repeating-linear-gradient(135deg,#b4532a_0_14px,#f6eedc_14px_22px,#4f5fe5_22px_36px,#f6eedc_36px_44px)]"
    />
  </Link>
);

/** Three member cards; they fan out when the tile is hovered. */
const MiniStack = () => (
  <div
    aria-hidden="true"
    className="absolute top-[22px] right-[18px] h-[260px] w-[210px] origin-top-right scale-50 md:top-auto md:-bottom-14 md:right-9 md:scale-100"
  >
    {[
      'top-0 bg-[linear-gradient(135deg,#1f2937,#0b0f17)] -rotate-[8deg] group-hover:-rotate-12 group-hover:-translate-x-3.5 group-hover:-translate-y-2.5',
      'top-14 bg-[linear-gradient(135deg,#e5484d,#a8242a)] rotate-[5deg] translate-x-3',
      'top-28 bg-[linear-gradient(135deg,#f5a524,#c26c08)] -rotate-2 group-hover:rotate-3 group-hover:-translate-x-1.5 group-hover:translate-y-3'
    ].map((card) => (
      <div
        key={card}
        className={`absolute left-0 h-[132px] w-[210px] rounded-[14px] shadow-[0_18px_30px_-12px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.35)] motion-safe:transition-transform motion-safe:duration-500 ${card}`}
      >
        <span className="barcode absolute inset-x-3.5 bottom-3 h-[30px] rounded-md px-2 py-1.5" />
      </div>
    ))}
  </div>
);

const MemberSheepTile = () => (
  <Link
    to={msPath('en', '/')}
    className={`${TILE} bg-[radial-gradient(120%_90%_at_90%_0%,var(--color-ms-blue-light)_0%,var(--color-ms-blue)_40%,var(--color-ms-blue-deep)_100%)] font-apple text-white [animation-delay:240ms]`}
  >
    <MiniStack />
    <img
      src={memberSheepIcon}
      alt=""
      width={84}
      height={84}
      className="h-[84px] w-[84px] rounded-[19px] shadow-[0_12px_30px_rgba(0,0,0,0.25),0_0_0_1px_rgba(255,255,255,0.3)]"
    />
    <h2 className="mt-7 font-display text-[46px] leading-none font-extrabold tracking-[-0.04em] md:text-[56px]">MemberSheep</h2>
    <p className="mt-3 max-w-[24ch] text-lg text-white/85">Every membership card, one flock. Kept on your iPhone.</p>
    <div className="mt-auto flex flex-wrap gap-2 pt-7 text-[13px]">
      {['iOS', MEMBERSHEEP_APP_STORE_URL ? 'On the App Store' : 'Coming soon'].map((tag) => (
        <span key={tag} className="rounded-lg border border-white/30 bg-white/15 px-2.5 py-1 backdrop-blur-md">
          {tag}
        </span>
      ))}
    </div>
    <span className={CTA}>
      Visit MemberSheep <span className={CTA_ARROW}>→</span>
    </span>
  </Link>
);

/** One tile per app, each in its own app's look. */
const AppTiles = () => (
  <section id="apps" aria-label="Apps" className="grid gap-5 md:grid-cols-2">
    <TravoTile />
    <MemberSheepTile />
  </section>
);

export default AppTiles;
